// lib/koi-pond-v3/interaction/dragonGatePhases.ts
//
// PURE math for the Dragon-Gate moon-click beat. No three.js / DOM imports
// so it runs under `tsx` for unit tests. dragonGate.ts (the three glue)
// converts these plain numbers into Vector3/Quaternion + uniform writes.
//
// One beat is DRAGON_GATE_DURATION seconds. Every channel below returns 0
// at rest (elapsed<0 or elapsed>duration) so the scene is untouched when
// no beat is running.

export const DRAGON_GATE_DURATION = 5.2;

// phase windows (seconds from click). The leap is long (0.7→3.1 = 2.4s) so the
// koi can HOLD at the apex (see leapWarp) — the held dragon silhouette is the
// payoff, and a brief arc reads as "a flash" (owner note 2026-06-14).
export const PH = {
  anticipateEnd: 0.35,
  detonate: 0.85,
  leapStart: 0.7,
  leapEnd: 3.1,
  reentry: 3.7,
} as const;

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);
const smooth = (a: number, b: number, x: number) => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};
// 0→1→0 hump peaking at `peak` within [a,b]
const hump = (a: number, peak: number, b: number, x: number) =>
  x < peak ? smooth(a, peak, x) : 1 - smooth(peak, b, x);

export interface DragonGateChannels {
  moonBloom: number;   // 0 rest → ~1 detonation, eases back; slight negative dip in anticipation
  shock: number;       // 0..1 shockwave strength envelope
  shockAge: number;    // 0..1 expansion progress (drives radius)
  caustic: number;     // 0..1 caustic flare
  flash: number;       // 0..1 color-grade warm flash
  scatter: number;     // 0..1 firefly scatter impulse (fires once at detonation; see note)
  shimmer: number;     // 0..1 koi dragon-scale glint (peaks at leap apex)
  reflectionBreak: number; // 0..1 splash/reflection-break trigger envelope (re-entry)
}

// elapsed = seconds since trigger. `reduced` = prefers-reduced-motion variant.
export function dragonGateChannels(elapsed: number, reduced = false): DragonGateChannels {
  const dead: DragonGateChannels = {
    moonBloom: 0, shock: 0, shockAge: 0, caustic: 0, flash: 0,
    scatter: 0, shimmer: 0, reflectionBreak: 0,
  };
  if (elapsed < 0 || elapsed > DRAGON_GATE_DURATION) return dead;

  if (reduced) {
    // Gentle: a soft moon bloom + a soft reflection break, nothing violent.
    return {
      ...dead,
      moonBloom: 0.5 * hump(0.0, 0.9, 4.6, elapsed),
      reflectionBreak: smooth(0.6, 1.4, elapsed) * (1 - smooth(3.0, 5.0, elapsed)),
    };
  }

  // anticipation dip then detonation bloom then long ease-back
  const dip = -0.18 * hump(0.0, PH.anticipateEnd * 0.6, PH.anticipateEnd, elapsed);
  const burst = hump(PH.anticipateEnd, PH.detonate, DRAGON_GATE_DURATION, elapsed);
  const moonBloom = dip + 1.0 * burst;

  const shock = hump(PH.anticipateEnd, PH.detonate, 3.4, elapsed);
  const shockAge = clamp01((elapsed - PH.anticipateEnd) / (3.4 - PH.anticipateEnd));
  const caustic = hump(PH.anticipateEnd, PH.detonate, 1.8, elapsed);
  const flash = hump(PH.anticipateEnd, 0.6, 1.4, elapsed);
  const scatter = smooth(PH.anticipateEnd, PH.detonate, elapsed); // step-ish; consumer fires impulse on rising edge
  const shimmer = hump(PH.leapStart, (PH.leapStart + PH.leapEnd) / 2, PH.leapEnd, elapsed);
  const reflectionBreak = smooth(PH.leapEnd, PH.reentry, elapsed) * (1 - smooth(PH.reentry + 0.4, DRAGON_GATE_DURATION, elapsed));

  return { moonBloom, shock, shockAge, caustic, flash, scatter, shimmer, reflectionBreak };
}

// Leap progress 0..1 over [leapStart, leapEnd]; <0 outside that window.
export function leapT01(elapsed: number): number {
  if (elapsed < PH.leapStart || elapsed > PH.leapEnd) return -1;
  return (elapsed - PH.leapStart) / (PH.leapEnd - PH.leapStart);
}

export interface Vec3 { x: number; y: number; z: number; }

// Parametric ballistic arc start→apex→end. Returns position + unit tangent.
// t01 in [0,1]. Apex is the control point at t01=0.5; we use a quadratic
// Bezier so the path is smooth and the tangent is analytic.
export function leapArc(start: Vec3, apex: Vec3, end: Vec3, t01: number): { pos: Vec3; tangent: Vec3 } {
  const u = 1 - t01;
  // quadratic bezier B(t) = u^2 P0 + 2 u t P1 + t^2 P2
  const pos: Vec3 = {
    x: u * u * start.x + 2 * u * t01 * apex.x + t01 * t01 * end.x,
    y: u * u * start.y + 2 * u * t01 * apex.y + t01 * t01 * end.y,
    z: u * u * start.z + 2 * u * t01 * apex.z + t01 * t01 * end.z,
  };
  // B'(t) = 2u(P1-P0) + 2t(P2-P1)
  const dx = 2 * u * (apex.x - start.x) + 2 * t01 * (end.x - apex.x);
  const dy = 2 * u * (apex.y - start.y) + 2 * t01 * (end.y - apex.y);
  const dz = 2 * u * (apex.z - start.z) + 2 * t01 * (end.z - apex.z);
  const len = Math.hypot(dx, dy, dz) || 1;
  return { pos, tangent: { x: dx / len, y: dy / len, z: dz / len } };
}

// Time-warp for the leap: linear progress s∈[0,1] → bézier param p∈[0,1] with a
// PLATEAU at 0.5 so the koi holds near-still at the apex (the held dragon-gate
// silhouette). smoothstep rise/fall = slow-mo ease around the hold. RISE is the
// fraction spent rising (and the same falling); the middle ~32% holds at p=0.5.
export function leapWarp(s: number): number {
  const RISE = 0.34;
  const ss = (a: number, b: number, x: number) => {
    const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
    return t * t * (3 - 2 * t);
  };
  if (s <= RISE) return 0.5 * ss(0, RISE, s);
  if (s >= 1 - RISE) return 0.5 + 0.5 * ss(1 - RISE, 1, s);
  return 0.5; // hold at apex
}

// Firefly scatter offset magnitude (0..1) over its life; quick out, slow back.
export function scatterDecay(age: number): number {
  if (age < 0 || age > 2.0) return 0;
  return clamp01(age / 0.18) * Math.max(0, 1 - age / 2.0);
}

// Is a click (NDC) within `radius` of the moon (NDC)? z-cull behind camera.
export function moonHit(clickX: number, clickY: number, moonX: number, moonY: number, moonZ: number, radius: number): boolean {
  if (moonZ >= 1) return false; // behind camera / clipped
  return Math.hypot(clickX - moonX, clickY - moonY) <= radius;
}
